import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import tw from 'twin.macro';

import { CloseIcon } from '@icons/CloseIcon';
import { Button } from '@components/Button';
import { Spacer } from '@components/Spacer';
import { deleteIntegrationTag } from '@insights_module/services/megaphone';
import { Loader } from '@components/Loader';
import { useNotification } from '@insights_module/hooks/useNotification';
import { INTEGRATION_QUERY_KEYS } from '@insights_module/constants/integrations';
import { useIntegrationTags } from '@insights_module/hooks/useIntegrationTags';
import { IntegrationType } from '@insights_module/types/Integrations';
import { useIntegrationsContext } from '@insights_module/context/IntegrationsContext';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { Modal } from '@components/Modal';

const Container = tw.div`w-full bg-neutral-background sm:max-w-[400px] m-auto border border-solid border-gray-1 rounded-lg overflow-hidden shadow-modal flex flex-col`;
const Header = tw.div`w-full px-4 py-3 flex items-center justify-between`;
const Body = tw.div`w-full h-full px-4 py-3`;
const Footer = tw.div`w-full px-4 py-3 flex items-center justify-end`;
const Title = tw.span`text-base text-text-and-icon text-left font-semibold`;
const ButtonsWrapper = tw.div`flex items-center justify-end gap-2`;
const ModalButton = tw(
  Button.Primary
)`text-xs leading-none px-3 py-[5px] w-auto h-[28px] border border-solid border-gray-1 align-middle text-text-and-icon`;
const TextBase = tw.p`text-left text-text-and-icon-muted text-[14px] leading-5`;
const TextNote = tw(TextBase)`font-semibold`;

export const DeleteTagModal = ({ type }: { type: IntegrationType }): JSX.Element | null => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { notify, NTFC_TYPE } = useNotification();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const {
    setIsDeleteTagModalOpen,
    isDeleteTagModalOpen,
    setTagToDelete,
    tagToDelete,
    selectedRows,
    setSelectedRows,
    searchTerm,
  } = useIntegrationsContext();

  const { isEnabled: isIHMStarshipIntegrationEnabled } = useFeatureFlag(
    FEATURE_FLAG.IHM_STARSHIP_INTEGRATION
  );

  const { getTagByQueryId } = useIntegrationTags(type, searchTerm);

  const _tag = getTagByQueryId(tagToDelete);
  const isBulk = selectedRows.length > 0;

  const handleClose = () => {
    setIsDeleteTagModalOpen(false);
    setTagToDelete(null);
  };

  const handleDelete = async () => {
    setIsLoading(true);

    if (isBulk) {
      Promise.all(
        selectedRows.map(async (id) => {
          await deleteIntegrationTag(id as string);
        })
      )
        .then(() => {
          notify({
            type: NTFC_TYPE.INFO,
            message: t('insights.integrations.megaphone.delete_tag_modal.notification.plural', {
              count: selectedRows.length,
            }),
          });

          queryClient.invalidateQueries([INTEGRATION_QUERY_KEYS.INFINITE_TAGS]);
          setSelectedRows([]);
        })
        .finally(() => {
          handleClose();
          setIsLoading(false);
        });
    } else {
      await deleteIntegrationTag(tagToDelete as string);

      setIsLoading(false);
      queryClient.invalidateQueries([INTEGRATION_QUERY_KEYS.INFINITE_TAGS]);

      handleClose();
      notify({
        type: NTFC_TYPE.INFO,
        message: t('insights.integrations.megaphone.delete_tag_modal.notification.singular'),
      });

      handleClose();
    }
  };

  if (!isDeleteTagModalOpen) {
    return null;
  }

  return (
    <Modal onClose={() => handleClose()}>
      <Container data-testid='delete-tag-modal'>
        <Header>
          <Title>
            {isBulk
              ? t('insights.integrations.megaphone.delete_tag_modal.title.plural')
              : t('insights.integrations.megaphone.delete_tag_modal.title.singular')}
          </Title>
          <ModalButton
            tw='bg-transparent border-none p-0'
            onClick={handleClose}
            data-testid='delete-modal-close-btn'>
            <CloseIcon tw='fill-text-and-icon' />
          </ModalButton>
        </Header>
        <Body>
          <TextBase>
            {isBulk
              ? t('insights.integrations.megaphone.delete_tag_modal.bulk_message', {
                  count: selectedRows.length,
                })
              : t('insights.integrations.megaphone.delete_tag_modal.message', {
                  tagName: _tag?.query.display_name,
                })}
          </TextBase>
          <Spacer direction='vertical' size='xs' />
          <TextNote>
            {isIHMStarshipIntegrationEnabled
              ? t('insights.integrations.ihm_starship.delete_tag_modal.note')
              : t('insights.integrations.megaphone.delete_tag_modal.note')}
          </TextNote>
        </Body>
        <Footer>
          <ButtonsWrapper>
            <ModalButton onClick={handleClose} tw='bg-transparent'>
              {t('common.cancel')}
            </ModalButton>
            <ModalButton
              tw='bg-error-bg border-error-bg w-[87px] whitespace-nowrap'
              onClick={handleDelete}>
              {isLoading ? (
                <Loader theme='dark' size='xs' />
              ) : isBulk ? (
                t('insights.integrations.megaphone.delete_tag_modal.cta.plural')
              ) : (
                t('insights.integrations.megaphone.delete_tag_modal.cta.singular')
              )}
            </ModalButton>
          </ButtonsWrapper>
        </Footer>
      </Container>
    </Modal>
  );
};
